// Rene omforminger av Proff-fila. Ingen Node-avhengigheter, ingen I/O.
//
// Skilt ut fra importer-leads.mjs av samme grunn som kandidater.ts og
// epostDomene.ts er skilt ut fra sine edge-funksjoner: dette er
// forretningsreglene, og de skal kunne enhetstestes uten å dra med seg
// `node:fs` og en innlogging mot prod.

/** «43222» → «43.222». Skjemaet bruker BRREGs punktform, fila gjør ikke. */
export function naeringskodeMedPunktum(v) {
  const siffer = String(v ?? "").replace(/\D/g, "");
  if (siffer.length < 4 || siffer.length > 5) return null;
  return `${siffer.slice(0, 2)}.${siffer.slice(2).padEnd(3, "0")}`;
}

/** Telefonnummer med mellomrom fjernet, eller null. */
export function telefonFelt(v) {
  const t = String(v ?? "").replace(/\s+/g, "");
  return /^[+\d][\d]{5,}$/.test(t) ? t : null;
}

/** Én e-postadresse i småbokstaver, eller null. */
export function epostFelt(v) {
  const e = String(v ?? "")
    .trim()
    .toLowerCase();
  return /^[^\s@,;]+@[^\s@,;]+\.[a-z]{2,}$/.test(e) ? e : null;
}

/** Domenedelen av en e-postadresse. */
export const domeneAv = (e) => (e ? e.split("@")[1] : null);

/**
 * Verter som aldri er en bedrifts eget nettsted.
 *
 * Målt i fila: 10 rader oppgir en Facebook-side som «nettsted». Å lagre den
 * ville gitt scan_light en side å måle som ikke er deres — vi ville scoret
 * Facebooks HTML, ikke bedriftens fravær av nettsted. At de IKKE har et eget
 * nettsted er dessuten det mest interessante signalet for et byrå som selger
 * nettsider, og det signalet forsvinner hvis vi later som om de har ett.
 *
 * Åtte av de ti fanges også av delt-vert-regelen i innlasteren. Lista er her
 * fordi de to siste ikke gjør det, og fordi regelen skal være uttalt.
 */
export const IKKE_EGET_NETTSTED = new Set([
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "youtube.com",
  "twitter.com",
  "x.com",
  "1881.no",
  "gulesider.no",
  "proff.no",
  "finn.no",
]);

/**
 * `INTERNET`-verdien → vertsnavnet vi lagrer, eller null.
 *
 * Husets format er et VERTSNAVN uten skjema — se hvordan resolve_urls lagrer
 * treffene sine (`f.url.replace(/^https?:\/\//, "")`) og hvordan radene i prod
 * faktisk ser ut: «www.expolight.no», «hope.no». `www.` beholdes som det står,
 * fordi det er slik de øvrige radene ser ut og fordi apex og www er samme
 * nettsted for fetch-en uansett.
 *
 * En sti kappes: 21 rader i fila har en (typisk /om-oss eller en
 * Facebook-profil). Vi skanner forsiden, ikke undersiden de valgte å oppgi.
 *
 * Verter med æ/ø/å beholdes som de er. safeFetch går gjennom URL-konstruktøren,
 * som punycode-koder dem automatisk — å folde dem til ascii her ville laget et
 * annet domene enn det som finnes.
 */
export function vertFraUrl(v) {
  const raa = String(v ?? "").trim();
  if (!raa) return null;
  const vert = raa
    .replace(/^https?:\/\//i, "")
    .split(/[/?#]/)[0]
    .replace(/\.+$/, "")
    .toLowerCase();
  // Minst ett punktum og et toppdomene på to bokstaver eller mer. Tillater
  // æøå og bindestrek; avviser porter, brukerinfo og tomme ledd.
  return /^[a-z0-9æøå](?:[a-z0-9æøå-]*[a-z0-9æøå])?(?:\.[a-z0-9æøå-]+)*\.[a-zæøå]{2,}$/.test(
    vert,
  )
    ? vert
    : null;
}

/** Verten uten `www.`, som er nøkkelen to rader deles på. */
export const utenWww = (vert) => vert.replace(/^www\./, "");

/**
 * `STIFTELSESDATO` → ISO-dato, eller null.
 *
 * Fila skriver «09.12.2005»; kolonnen i basen er `date`, som vil ha
 * «2005-12-09». Målt på fila: alle 4898 radene har formatet, ingen er ugyldige,
 * og årene spenner 1914–2025.
 *
 * Kalenderkontrollen er ikke overflødig. `new Date("2020-02-31")` kaster ikke —
 * den ruller over til 2. mars. Uten sammenlikningen mot inndata ville en
 * skrivefeil i fila blitt lagret som en dato som finnes, bare en annen enn den
 * som sto der.
 */
export function stiftelsesdatoFelt(v) {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(v ?? "").trim());
  if (!m) return null;
  const [, dag, maaned, aar] = m;
  const iso = `${aar}-${maaned}-${dag}`;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10) === iso ? iso : null;
}

/**
 * `REG. I MERVERDIAVG` («Ja»/«Nei») → boolean, eller null.
 *
 * TOMT FELT ER UKJENT, IKKE «NEI». 31 rader i fila har blankt her, og å lagre
 * dem som `false` ville påstått at de ikke er mva-registrerte — en påstand fila
 * ikke gjør. Kolonnen er nullable nettopp for å kunne skille «vet ikke» fra
 * «nei», og berikelsen fra Enhetsregisteret fyller dem senere.
 */
export function jaNeiFelt(v) {
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  if (s === "ja") return true;
  if (s === "nei") return false;
  return null;
}

/**
 * Et regnskapsbeløp fra fila → hele kroner, eller null.
 *
 * FILA OPPGIR TUSEN NOK. «3378» er 3 378 000, ikke 3378. Å lagre tallet slik
 * det står ville lagt en usynlig faktor 1000 i en kolonne som heter
 * `omsetning` — og den slags enhetsfeil oppdages først når noen har ganget med
 * tusen én gang for mye et helt annet sted.
 *
 * Negative beløp er gyldige: et årsresultat kan være under null, og det er
 * nettopp da det sier noe.
 *
 * Desimaler forekommer ikke i beløpskolonnene (kontrollert mot fila), men
 * komma godtas som desimalskilletegn hvis en fremtidig eksport bruker det.
 * Resultatet rundes til hele kroner — en øre-presisjon på et tusentall er
 * uansett innbilt.
 */
export function belopTusenTilKroner(v) {
  const raa = String(v ?? "")
    .trim()
    // \s dekker også hardt mellomrom (U+00A0), som er tusenskillet Excel og
    // Proff bruker. Å skrive tegnet selv i mønsteret ville vært både
    // overflødig og usynlig i en diff.
    .replace(/\s/g, "")
    .replace(",", ".");
  if (!raa || !/^-?\d+(\.\d+)?$/.test(raa)) return null;
  const tall = Number(raa);
  return Number.isFinite(tall) ? Math.round(tall * 1000) : null;
}

/**
 * `PERIODE SLUTT` («31.12.2025») → regnskapsåret, eller null.
 *
 * Året tas fra periodens SLUTT, ikke start: et avvikende regnskapsår som går
 * fra 01.07.2024 til 30.06.2025 er 2025-regnskapet, og det er sluttdatoen som
 * bestemmer hvor ferskt tallet er.
 *
 * Grensene 1900–2100 speiler sjekkbetingelsen i basen, så en verdi som ville
 * blitt avvist der stoppes her i stedet — med en tom kolonne, ikke en feilet
 * innlasting av 4898 rader.
 */
export function regnskapsaarFraPeriode(v) {
  const iso = stiftelsesdatoFelt(v);
  if (!iso) return null;
  const aar = Number(iso.slice(0, 4));
  return aar >= 1900 && aar <= 2100 ? aar : null;
}

/** ISO-4217-kode i store bokstaver, eller null. */
export function valutaFelt(v) {
  const k = String(v ?? "")
    .trim()
    .toUpperCase();
  return /^[A-Z]{3}$/.test(k) ? k : null;
}

/**
 * De fire regnskapsfeltene, normalisert under ett.
 *
 * ALT ELLER INGENTING PÅ ÅR OG VALUTA. Mangler ett av dem, returneres alle
 * fire som null — også beløpene. Et beløp uten år kan ikke vurderes for
 * ferskhet, og et beløp uten valuta er ikke et beløp: scoringen sammenlikner
 * mot norske kronegrenser, og en omsetning i SEK ville da blitt lest som ~10 %
 * for høy uten at noe så galt ut. Å lagre halve opplysningen ville gitt
 * scoringen noe den likevel må forkaste, men som ser komplett ut i basen.
 */
export function regnskapFelter({
  omsetning,
  aarsresultat,
  periodeSlutt,
  valuta,
} = {}) {
  const tomt = {
    omsetning: null,
    aarsresultat: null,
    regnskapsaar: null,
    regnskap_valuta: null,
  };
  const aar = regnskapsaarFraPeriode(periodeSlutt);
  const kode = valutaFelt(valuta);
  if (aar === null || kode === null) return tomt;
  return {
    omsetning: belopTusenTilKroner(omsetning),
    aarsresultat: belopTusenTilKroner(aarsresultat),
    regnskapsaar: aar,
    regnskap_valuta: kode,
  };
}

/**
 * Kandidatraden → det som faktisk skal med i upserten.
 *
 * Tre felter tas ut, og det er ikke pynt. Upserten kjører med
 * `onConflict: "org_nr"`, så alt som ligger i payloaden skrives PÅ NYTT ved
 * hver konflikt. For en rad som allerede finnes ville det byttet ut
 *
 *   url             – en adresse resolveren fant eller et menneske rettet,
 *   stiftelsesdato  – og
 *   registrert_mva  – verdier berikelsen alt har hentet førstehånds
 *
 * med fila sin annenhånds utgave. De skrives i stedet i egne steg med
 * `.is(felt, null)`, som gjør skrivingen til et krav om å være FØRST, ikke om
 * å ha rett.
 *
 * Egen funksjon fordi lista er lett å glemme neste gang et felt legges til, og
 * fordi konsekvensen av å glemme den er stille datatap i prod.
 */
export function radForUpsert({
  url: _url,
  stiftelsesdato: _stiftelsesdato,
  registrert_mva: _registrertMva,
  ...rest
}) {
  return rest;
}

// --- Organisasjonsnummer ----------------------------------------------------
//
// Flyttet hit fra felles.mjs (30.07.2026) fordi de nå trengs to steder:
// skriptene og NETTLESEREN. felles.mjs importerer node:fs og kan ikke bygges
// inn i appen; denne modulen er ren og kan.

/** Siffer ut, ni siffer inn — ellers null. Tåler «NO 123 456 789». */
export function normaliserOrgNr(v) {
  const siffer = String(v ?? "").replace(/\D/g, "");
  return /^\d{9}$/.test(siffer) ? siffer : null;
}

/**
 * Mod-11-kontrollsifferet i et organisasjonsnummer.
 *
 * Målt på Proff-fila var alle 4898 gyldige, så dette er ikke en filtrering vi
 * forventer at gjør noe. Den er der fordi et org.nr med trykkfeil ville blitt
 * en ny, evig rad i universet som aldri kan berikes fra BRREG — en feil som er
 * billig å hindre og dyr å rydde.
 */
export function orgNrGyldig(orgNr) {
  if (!/^\d{9}$/.test(orgNr)) return false;
  const vekt = [3, 2, 7, 6, 5, 4, 3, 2];
  const sum = vekt.reduce((s, v, i) => s + v * Number(orgNr[i]), 0);
  const rest = 11 - (sum % 11);
  if (rest === 10) return false;
  return (rest === 11 ? 0 : rest) === Number(orgNr[8]);
}

/**
 * Grupper rader etter hvilke felt som faktisk er utfylt, og fjern de tomme.
 *
 * DETTE ER IKKE PYNT. PostgREST bygger kolonnelista for `on conflict do
 * update` av UNIONEN av nøkler i porsjonen. Sender vi `telefon: null` for en
 * rad fila mangler telefon for, blir det til `set telefon = null` — og en rad
 * som allerede finnes i universet ville fått BRREG-telefonen slettet. Samme
 * feilklasse som slettet nettstedene i #261, bare på en annen kolonne.
 *
 * Bor her, og ikke i felles.mjs der den ble brukt først, fordi innlastingen fra
 * nettleseren må gruppere på nøyaktig samme måte. To utgaver av denne regelen
 * ville betydd at den ene glemmer et felt.
 */
export function grupperPaaUtfylteFelt(rader) {
  const grupper = new Map();
  for (const rad of rader) {
    const nokler = Object.keys(rad)
      .filter((k) => rad[k] !== undefined && rad[k] !== null)
      .sort();
    const signatur = nokler.join(",");
    const renset = Object.fromEntries(nokler.map((k) => [k, rad[k]]));
    if (!grupper.has(signatur)) grupper.set(signatur, []);
    grupper.get(signatur).push(renset);
  }
  return [...grupper.values()];
}
